<template>
  <div class="video-settings" v-if="state === ''">
    <card-minimal title="Description">
      <div class="d-f fd-c fd-md-r p-t-3 p-b-2 p-x-1">
        <div class="p-x-3 f-1">
          <p class="card-content-title">
            <strong>Title</strong>
          </p>
          <form-input type="text" ref="title" v-model="data.title"  />
          <p class="card-content-title m-t-2">
            <strong>Published</strong>
          </p>
          <form-input type="datetime-local" :value="data.published" :max="maxDate" @input="data.published = $event" />
          <p class="card-content-title m-t-2">
            <strong>Description</strong>
          </p>
          <form-textarea class="small m-b-2" :toolbar="['bold', 'ul', 'link']" height="185" v-model="data.description" />
          <p class="card-content-title m-t-2">
            <strong>Tags</strong>
            <span>Automatically links video to collections that has the same tags</span>
          </p>
          <FormTags class="f-w m-b-1" placeholder="Add tags" :existingTags="data.tags" @data="setTags" v-if="data.tags !== null && done"/>
          <template v-if="collections.length > 0">
            <div class="d-f f-wrap ai-c m-b-2" style="line-height: 18px;">
              <p style="color: gray; font-size: 14px; margin-right: 5px;">Collection{{ collections.length > 1 ? 's' : ''}}:</p>
              <!-- eslint-disable-next-line -->
              <router-link v-for="(collection, index) in collections" :key="collection.id" :to="`/collection/${collection.id}`" class="link blue m-r-1">{{ collection.metadata.title }}{{ index < collections.length - 1 ? ',' : ''}}</router-link>
            </div>
          </template>
          <template v-else>
            <p style="color: gray; font-size: 14px; margin-right: 5px;">No collections matched</p>
          </template>
        </div>
        <div class="p-x-3 f-1">
          <p class="card-content-title">
            <strong>Poster image</strong>
          </p>
          <media-upload class="m-b-2" :legacy="true" @images="setImage" :unsplash="true" :multiple="false" title="Poster image" :overwrite="true" size="h/768" :data="{ force: true, images: data.currentPoster ? [{ value: data.currentPoster }] : [] }" />
        </div>
      </div>
      <div class="p-x-4 p-b-4" v-if="video">
        <Button :class="`large ${canUpdate ? '' : 'disable'}`" :loading="updating" @click="update">Update</Button>
      </div>
    </card-minimal>
    <card-minimal title="Features" class="m-t-4" v-if="[1, 6, 15, 16].includes(user.permission_id)">
      <div class="features p-a-3">
        <toggle class="group" :value="!data.isPrivate" @change="togglePrivate" text="Visibility" :explanation="liveshoppingEnabled && featuresEnabled.includes('lvs') ? 'Enable embeding of your video' : 'Show the video on your Streamify Play site'" />
        <toggle class="group" :value="data.ppv" @change="togglePPV" text="Pay Per View" explanation="Sell access to this video" v-if="ppvEnabled && featuresEnabled.includes('ppv')" />
      </div>
    </card-minimal>
    <card-minimal id="embed" title="Embed the player" class="m-t-4" v-if="video">
      <div class="p-t-4 p-b-3 p-x-4" v-if="video.private">
        <p class="card-content-title">
          <strong>You must enable the Visibility setting above in order to embed this video.</strong>
        </p>
      </div>
      <div class="d-f fd-c fd-md-r" v-else>
        <div class="f-1 p-x-3 side-border-right p-a-4">
          <p class="card-content-title">
            <strong>Configure embed</strong>
          </p>
          <Button class="extra-sidepadding f-w" @click="openVideoPreview">Configure & Preview</Button>
        </div>
        <div class="f-3 p-x-3 p-x-4 p-b-4 p-t-md-4">
          <p class="card-content-title">
            <strong>Quick embed</strong>
            <span>Code to include Video Player on your site</span>
          </p>
          <form-input type="text" ref="title" class="mono" :value="embedCode" :readonly="true" :copy="true" />
          <Toggle :text="$t('broadcast.include_javascript')" @change="embedCodeWithScript = $event" class="small m-t-1 embed-title" />
        </div>
      </div>
    </card-minimal>
  </div>
</template>

<script>

import moment from 'moment';

import Toggle from '@/components/toggle.vue';
import MediaUpload from '@/components/MediaUpload.vue';

export default {
  components: {
    MediaUpload,
    Toggle,
  },
  computed: {
    /**
     * Check if all fields are valid to update a video
     * @returns {Boolean}
     */
    canUpdate() {
      const {
        data: {
          title,
        },
      } = this;

      if (title === '') {
        return false;
      }

      return true;
    },
    /**
     * Video
     * @return {?Object}
     */
    video() {
      return this.$store.getters['Video/current'];
    },
    /**
     * Return an embed code for a play-broadcast
     * @returns {String}
     */
    /*
    embedCode() {
      if (!this.video) {
        return '';
      }
      const { embedCodeWithTitle, embedCodeWithAutoplay, fqdn } = this;
      return `<style>.sb-embed-container { position: relative; padding: 0 0 56.25% 0; height: 0; } .sb-embed-container iframe { border: 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; }</style><div class="sb-embed-container"><iframe src="https://${fqdn}.streamify.io/play/${embedCodeWithTitle ? 'broadcast' : 'player'}/${this.video.id}?embed${embedCodeWithAutoplay ? '&autoplay' : ''}" frameborder="0" allowfullscreen></iframe></div>`;
    },
    */
    /**
     * Return a directlink to broadcast on play-site
     * @returns {String}
     */
    directLink() {
      if (!this.video) {
        return '';
      }
      return `https://${this.fqdn}.streamify.io/play/broadcast/${this.video.id}`;
    },
    previewAddress() {
      return `https://player.streamify.io/#/v?id=${this.video.id}`;
    },
    embedCode() {
      if (!this.video) {
        return '';
      }
      const {
        video: {
          id,
        },
        embedCodeWithScript,
      } = this;
      // eslint-disable-next-line
      return `${embedCodeWithScript ? '<script src="https://cdn.streamify.io/streamify-player.js"><\/script>' : ''}<streamify-player id="${id}"><\/streamify-player>`;
    },
  },
  methods: {
    openVideoPreview() {
      window.open(this.previewAddress, '_blank');
    },
    /**
     * Toggle Private
     * @param {Boolean} bool
     * @returns {Void}
     */
    async togglePrivate(bool) {
      this.data.isPrivate = !bool;
      await this.$store.dispatch('Video/update', {
        id: this.video.id,
        data: {
          private: this.data.isPrivate,
        },
      });
    },
    /**
     * Toggle PPV
     * @param {Boolean} bool
     * @returns {Void}
     */
    async togglePPV(bool) {
      this.data.ppv = bool;
      await this.$store.dispatch('Video/update', {
        id: this.video.id,
        data: {
          protected: {
            pay_per_view: {
              enabled: this.data.ppv,
            },
          },
        },
      });
    },
    /**
     * Update a video with data
     * @returns {Void}
     */
    async update() {
      if (!this.canUpdate) {
        return;
      }

      const {
        title,
        description,
        published,
        tags,
        currentPoster,
        newPoster,
      } = this.data;

      this.updating = true;

      await this.$store.dispatch('Video/update', {
        id: this.video.id,
        data: {
          metadata: {
            title,
            description,
          },
          published: moment(published).toISOString(),
          tags,
        },
      });

      if (currentPoster !== newPoster) {
        await this.$store.dispatch('Video/uploadPoster', { id: this.video.id, file: newPoster });
      }

      // await this.fetchVideoItems();
      setTimeout(() => {
        this.updating = false;
      }, 1000);
    },
    /**
     * Set a new poster image for the collection
     * @param {FormData} image
     * @returns {Void}
     */
    setImage(image) {
      this.data.newPoster = image;
    },
    setVideoData() {
      const { video } = this;
      const [poster] = video.resources.filter(item => (typeof item.value !== 'undefined' && item.value.indexOf('1097x617') >= 0 ? item : null));

      this.data = {
        title: video.metadata.title,
        description: video.metadata.description,
        isPrivate: video.private,
        ppv: video.protected.pay_per_view.enabled,
        currentPoster: poster ? `https://${this.fqdn}.streamify.io/resources/${poster.value}` : null,
        newPoster: poster ? `https://${this.fqdn}.streamify.io/resources/${poster.value}` : null,
        tags: video.tags,
        published: moment(video.published, 'YYYY-MM-DD HH:mm:ss Z z').format('Y-MM-DDTHH:mm'),
      };
    },
    setTags(tags) {
      this.data.tags = tags;

      if (this.data.tags.length > 0) {
        this.getCollections();
      } else {
        this.collections = [];
      }
    },
    /**
     * Get collections
     */
    async getCollections() {
      const {
        data: {
          tags,
        },
      } = this;

      if (tags.length > 0) {
        this.collections = await this.$store.dispatch('Collections/getBroadcastCollections', tags);
        return;
      }

      this.collections = [];
    },
  },
  data() {
    return {
      data: {
        title: '',
        ppv: false,
        isPrivate: false,
        description: '',
        currentPoster: null,
        newPoster: null,
        tags: [],
      },
      maxDate: moment().format('YYYY-MM-DDTHH:mm'),
      updating: false,
      state: 'loading',
      collections: [],
      done: false,
      embedCodeWithTitle: false,
      embedCodeWithAutoplay: false,
      embedCodeWithScript: false,
    };
  },
  mounted() {
    this.setVideoData();

    this.done = true;

    this.$store.dispatch('Tags/getTags', true);

    this.state = '';
  },
};
</script>

<style lang="scss">
.video-settings {
  .media-upload {
    .card-minimal {
      .card-title {
        display: none;
      }
      .card-content {
        border-radius: 0;
        box-shadow: unset;

        & > .p-a-3 {
          padding: 0;
        }
      }
    }
    .image-gallery {
      .images {
        background-position: 50% 50%;
        background-size: cover;
      }
    }
    .upload-area:after, .image-gallery .images {
      padding-bottom: 56.6%;
      padding-top: 0;
    }
  }

  .features {
    display: flex;
    flex-wrap: wrap;

    & > * {
      width: calc(100% - 12px);
      margin: 6px;
    }

    @media (min-width: 768px) {
      & > * {
        width: calc(50% - 12px);
      }
    }
  }
}
</style>
